import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Clock, 
  ShieldCheck, 
  Mountain, 
  Heart,
  XCircle,
  CreditCard,
  CheckCircle2,
  Truck,
  ShoppingBag,
  Mail,
  Phone,
  ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import VendorForm from '../components/VendorForm';
import { festivalInfo, programSchedule, whyJoinReasons, vendorCategories, importantRules, pricingConditions, lineupImages, heroImage } from '../data/mockData';

const VendorLanding = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  const scrollToForm = () => {
    setShowForm(true);
    setTimeout(() => {
      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const iconComponents = {
    Users, Clock, Calendar, ShieldCheck, Mountain, Heart, XCircle, CreditCard
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-600 hover:text-orange-500"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Přehled akcí</span>
            </Button>
            <img 
              src="https://www.kontaktfest.cz/wp-content/uploads/2025/12/kf-logo-3-1.png"
              alt="KONTAKT Fest"
              className="h-12"
            />
          </div>
          <Button 
            onClick={scrollToForm}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-2 rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            PŘIHLÁSIT SE
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 text-white overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage}
            alt="KONTAKT Fest Crowd"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/85"></div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="mb-8">
            <h1 className="text-6xl md:text-7xl font-bold mb-4">
              NABÍDKA PRO<br/>
              <span className="text-orange-500">FOOD TRUCKY</span><br/>
              A PRODEJCE
            </h1>
          </div>
          
          <div className="text-3xl md:text-4xl font-bold mb-8 text-orange-500">
            13. - 14. 6. 2026
          </div>

          <div className="text-xl md:text-2xl mb-12 text-gray-300 max-w-3xl mx-auto">
            FRENŠTÁT p. R. - TROJANOVICE
          </div>

          <Button 
            onClick={scrollToForm}
            size="lg"
            className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-12 py-7 font-bold rounded-full shadow-xl hover:shadow-2xl transition-all"
          >
            PŘIHLÁSIT SE NYNÍ
          </Button>
        </div>
      </section>

      {/* Festival Stats */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-b">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-orange-500 mb-2">{festivalInfo.expectedVisitors}</div>
              <div className="text-gray-700 font-medium">Očekávaných návštěvníků</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-orange-500 mb-2">2 dny</div>
              <div className="text-gray-700 font-medium">Prodejní dny</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-orange-500 mb-2">100%</div>
              <div className="text-gray-700 font-medium">Vyprodáno 2025</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-orange-500 mb-2">25-45</div>
              <div className="text-gray-700 font-medium">Let (cílová skupina)</div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">ATMOSFÉRA FESTIVALU</h2>
            <p className="text-xl text-gray-600">Podívejte se, jak vypadal KONTAKT Fest 2025</p>
          </div>
          
          <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl">
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/kNiePlLazOU"
              title="KONTAKT Fest 2025"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">FESTIVAL, KTERÝ SPOJUJE</h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Dva dny plné hudby, inspirativních přednášek a autentických rozhovorů v krásném prostředí Beskyd.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyJoinReasons.map((reason, index) => {
              const IconComponent = iconComponents[reason.icon];
              return (
                <div key={index} className="text-center p-6">
                  <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{reason.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Program Schedule */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">LINE-UP 2026</h2>
            <p className="text-xl text-gray-600">Objevte nejlepší umělce české hudební scény</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Saturday Lineup */}
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img 
                src={lineupImages.saturday}
                alt="Line-up Sobota 13. 6. 2026"
                className="w-full h-auto"
              />
            </div>

            {/* Sunday Lineup */}
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img 
                src={lineupImages.sunday}
                alt="Line-up Neděle 14. 6. 2026"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Conditions */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">PODMÍNKY PRO PRODEJCE</h2>
            <p className="text-xl text-gray-300">Cenová nabídka a technické požadavky</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Food Trucks */}
            <div className="bg-orange-500 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-3">{pricingConditions.foodTrucks.title}</h3>
              <div className="text-orange-100 mb-4">
                <p className="text-lg">{pricingConditions.foodTrucks.size}</p>
                <p className="text-sm opacity-90">{pricingConditions.foodTrucks.category}</p>
              </div>
              <div className="text-4xl font-bold">{pricingConditions.foodTrucks.price}</div>
            </div>

            {/* Additional Sales */}
            <div className="bg-white text-slate-900 rounded-lg p-8 border-2 border-gray-200">
              <h3 className="text-2xl font-bold mb-3">{pricingConditions.additional.title}</h3>
              <div className="text-gray-600 mb-4">
                <p className="text-lg">{pricingConditions.additional.size}</p>
                <p className="text-sm">{pricingConditions.additional.category}</p>
              </div>
              <div className="text-4xl font-bold text-orange-500">{pricingConditions.additional.price}</div>
            </div>
          </div>

          {/* Electricity & Deposit */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Electricity */}
            <div className="bg-gray-800 rounded-lg p-8">
              <h3 className="text-2xl font-bold mb-4 text-orange-500">{pricingConditions.electricity.title}</h3>
              <div className="space-y-3">
                {pricingConditions.electricity.options.map((option, index) => (
                  <div key={index} className="flex justify-between items-center border-b border-gray-700 pb-2">
                    <span className="text-gray-300">{option.range}</span>
                    <span className="font-bold text-xl">{option.price}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-400 text-sm mt-4">{pricingConditions.electricity.note}</p>
            </div>

            {/* Deposit */}
            <div className="bg-gray-800 rounded-lg p-8 flex flex-col justify-center">
              <h3 className="text-2xl font-bold mb-4 text-orange-500">{pricingConditions.deposit.title}</h3>
              <div className="text-5xl font-bold mb-4">{pricingConditions.deposit.price}</div>
              <p className="text-gray-300 text-lg">Vrací se po ukončení akce a kontrole stánku</p>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <div className="bg-orange-500/20 border-2 border-orange-500 rounded-lg p-6">
              <p className="text-orange-500 font-bold text-lg mb-2">⚠️ SPECIÁLNÍ NABÍDKA - SOBOTA (Open Stage)</p>
              <p className="text-white text-lg leading-relaxed">{pricingConditions.specialNote}</p>
            </div>
            
            <div className="text-center">
              <p className="text-gray-400 text-lg italic">{pricingConditions.note}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Old Program Schedule - REMOVED */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" style={{display: 'none'}}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">PROGRAM FESTIVALU</h2>
            <p className="text-xl text-gray-600">Dva plnohodnotné prodejní dny s různým charakterem programu</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-slate-900 p-8 text-white text-center">
                <h3 className="text-3xl font-bold mb-2">{programSchedule.saturday.day}</h3>
                <div className="flex items-center justify-center gap-2 text-orange-500 text-xl font-bold">
                  <Clock className="w-6 h-6" />
                  <span>{programSchedule.saturday.time}</span>
                </div>
              </div>
              <div className="p-8 bg-white">
                <p className="text-gray-700 text-lg mb-4">{programSchedule.saturday.description}</p>
                <div className="flex items-center gap-2 text-orange-500 font-semibold">
                  <CheckCircle2 className="w-5 h-5" />
                  <span>Hlavní prodejní špička</span>
                </div>
              </div>
            </div>

            <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-slate-900 p-8 text-white text-center">
                <h3 className="text-3xl font-bold mb-2">{programSchedule.sunday.day}</h3>
                <div className="flex items-center justify-center gap-2 text-orange-500 text-xl font-bold">
                  <Clock className="w-6 h-6" />
                  <span>{programSchedule.sunday.time}</span>
                </div>
              </div>
              <div className="p-8 bg-white">
                <p className="text-gray-700 text-lg mb-4">{programSchedule.sunday.description}</p>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-orange-900 font-bold">🎵 {programSchedule.sunday.specialEvent}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vendor Categories */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">KOHO HLEDÁME?</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Vybíráme prodejce s důrazem na kvalitu, kapacitu a zkušenosti
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Priority - Food Trucks */}
            <div className="border-2 border-orange-500 rounded-lg overflow-hidden">
              <div className="bg-orange-500 p-8">
                <div className="flex items-center gap-4 mb-4">
                  <Truck className="w-12 h-12 text-white" />
                  <div>
                    <div className="text-sm font-bold uppercase tracking-wider mb-1">Priorita</div>
                    <h3 className="text-3xl font-bold">{vendorCategories.priority.title}</h3>
                  </div>
                </div>
                <p className="text-orange-50 text-lg">{vendorCategories.priority.description}</p>
              </div>
              <div className="bg-white p-8 text-gray-900">
                <ul className="space-y-4 mb-6">
                  {vendorCategories.priority.requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
                      <span className="text-lg">{req}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-orange-50 border-l-4 border-orange-500 p-4">
                  <p className="text-sm text-gray-700">
                    <strong className="text-gray-900">Důvod:</strong> {vendorCategories.priority.reason}
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Sales */}
            <div className="border-2 border-gray-600 rounded-lg overflow-hidden">
              <div className="bg-gray-700 p-8">
                <div className="flex items-center gap-4 mb-4">
                  <ShoppingBag className="w-12 h-12 text-white" />
                  <div>
                    <h3 className="text-3xl font-bold">{vendorCategories.additional.title}</h3>
                  </div>
                </div>
                <p className="text-gray-200 text-lg">{vendorCategories.additional.description}</p>
              </div>
              <div className="bg-white p-8 text-gray-900">
                <ul className="space-y-4">
                  {vendorCategories.additional.categories.map((cat, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-3"></div>
                      <span className="text-lg">{cat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Rules */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">DŮLEŽITÁ PRAVIDLA</h2>
            <p className="text-xl text-gray-600">Podmínky, které je nutné respektovat</p>
          </div>

          <div className="space-y-6">
            {importantRules.map((rule, index) => {
              const IconComponent = iconComponents[rule.icon];
              return (
                <div key={index} className="border-2 border-red-300 rounded-lg bg-red-50 p-8">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-slate-900 mb-3">{rule.title}</h3>
                      <p className="text-gray-700 text-lg mb-3">{rule.description}</p>
                      {rule.note && (
                        <p className="text-base text-gray-600 italic mb-3">💡 {rule.note}</p>
                      )}
                      {rule.warning && (
                        <div className="mt-4 p-4 bg-red-100 border-l-4 border-red-500 rounded">
                          <p className="text-red-900 font-bold">⚠️ {rule.warning}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      {showForm && (
        <section id="application-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">PŘIHLÁŠKA</h2>
              <p className="text-xl text-gray-600">Vyplňte formulář a staňte se součástí KONTAKT Fest 2026</p>
            </div>
            <VendorForm />
          </div>
        </section>
      )}

      {/* CTA Section */}
      {!showForm && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">PŘIPRAVENI SE PŘIHLÁSIT?</h2>
            <p className="text-xl text-gray-300 mb-10 leading-relaxed max-w-3xl mx-auto">
              Limitovaný počet míst. Ročník 2025 byl kompletně vyprodán. 
              Zajistěte si své místo na jednom z nejlépe navštěvovaných festivalů v Beskydech.
            </p>
            <Button 
              onClick={scrollToForm}
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-12 py-7 font-bold rounded-full shadow-xl hover:shadow-2xl transition-all"
            >
              VYPLNIT PŘIHLÁŠKU
            </Button>
          </div>
        </section>
      )}

      {/* More Events Section */}
      <section className="py-8 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white border-y-2 sm:border-y-4 border-orange-400">
        <div className="max-w-5xl mx-auto text-center">
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl sm:rounded-2xl p-6 sm:p-12 border-2 sm:border-4 border-orange-300 shadow-xl">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-black mb-3 sm:mb-4 text-orange-700">
              🎪 Máme více festivalů!
            </h2>
            <p className="text-base sm:text-xl text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Zajímá vás prodejní místo i na dalších akcích? Podívejte se na všechny naše festivaly v roce 2026.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white text-base sm:text-xl px-6 sm:px-12 py-4 sm:py-6 font-black rounded-full shadow-2xl hover:shadow-3xl transition-all w-full sm:w-auto"
            >
              🎉 Zobrazit další akce
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16 px-4 sm:px-6 lg:px-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <img 
                src="https://www.kontaktfest.cz/wp-content/uploads/2025/12/kf-logo-3-1.png"
                alt="KONTAKT Fest"
                className="h-16 mb-6"
              />
              <p className="text-gray-400 leading-relaxed">
                Dvoudenní hudebně-lifestyle festival v srdci Beskyd
              </p>
            </div>
            <div>
              <h4 className="text-xl font-bold mb-6 text-orange-500">Kontakt</h4>
              <div className="space-y-3 text-gray-400">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-orange-500" />
                  <a href="mailto:monika@vseproakci.cz" className="hover:text-orange-500 transition-colors">
                    monika@vseproakci.cz
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-orange-500" />
                  <a href="tel:+420773550880" className="hover:text-orange-500 transition-colors">
                    +420 773 550 880
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-orange-500" />
                  <span>Frenštát p. R. - Trojanovice</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-xl font-bold mb-6 text-orange-500">Informace</h4>
              <div className="space-y-3">
                <a 
                  href="https://www.kontaktfest.cz/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="block text-gray-400 hover:text-orange-500 transition-colors"
                >
                  Oficiální web festivalu
                </a>
                <p className="text-gray-400">
                  Amfiteátr na Horečkách<br/>
                  Areál nad skokanskými můstky
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2026 KONTAKT Fest. Všechna práva vyhrazena.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VendorLanding;
